import { useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ContentBody from "./ContentBody";

const API = process.env.REACT_APP_BACKEND_URL;

const km = (meters) => (meters / 1000).toFixed(1);
const dur = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}u ${m}m` : `${m}m`;
};
const dateStr = (iso) => new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });

function SortHeader({ field, sortBy, sortDir, onSort, children }) {
  const active = sortBy === field;
  return (
    <th className={`sortable-header${active ? " sort-active" : ""}`} onClick={() => onSort(field)}>
      {children}{active ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
    </th>
  );
}

export default function Athletes() {
  const { athleteId } = useParams();
  const [athletes, setAthletes] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [content, setContent] = useState(null);
  const [sortBy, setSortBy] = useState("eventKm");
  const [sortDir, setSortDir] = useState("desc");
  const detailRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/athletes-list`)
      .then(r => r.json())
      .then(data => setAthletes(Array.isArray(data) ? data : []));
    fetch(`${API}/api/content/deelnemers`)
      .then(r => r.json())
      .then(setContent)
      .catch(() => {});
    fetch(`${API}/activities`)
      .then(r => r.json())
      .then(data => setAllActivities(Array.isArray(data) ? data : []));
  }, []);

  useEffect(() => {
    if (athleteId && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [athleteId]);

  const leaderboard = useMemo(() => {
    const map = {};
    athletes.forEach(a => {
      map[a._id] = { athlete: a, trainingKm: 0, eventKm: 0, trainingCount: 0, eventCount: 0 };
    });
    allActivities.forEach(a => {
      const id = a.athleteId?._id;
      if (!id || !map[id]) return;
      if (a.isTraining) {
        map[id].trainingKm += a.distance || 0;
        map[id].trainingCount++;
      } else {
        map[id].eventKm += a.distance || 0;
        map[id].eventCount++;
      }
    });
    return Object.values(map);
  }, [athletes, allActivities]);

  const sorted = useMemo(() => {
    return [...leaderboard].sort((a, b) => {
      let diff;
      if (sortBy === "name") {
        const na = a.athlete.displayName || a.athlete.name || "";
        const nb = b.athlete.displayName || b.athlete.name || "";
        diff = na.localeCompare(nb, "nl");
      } else {
        diff = b[sortBy] - a[sortBy];
      }
      return sortDir === "asc" ? -diff : diff;
    });
  }, [leaderboard, sortBy, sortDir]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortBy(field); setSortDir("desc"); }
  };

  const selectedActivities = useMemo(() =>
    allActivities.filter(a => a.athleteId?._id === athleteId),
    [allActivities, athleteId]
  );
  const selectedAthlete = athletes.find(a => a._id === athleteId);
  const selectedTrainings = selectedActivities.filter(a => a.isTraining);
  const selectedEvents = selectedActivities.filter(a => !a.isTraining);

  return (
    <div className="pane">
      <h1>{content?.title || "Atleten"}</h1>
      <ContentBody data={content} />

      <table className="leaderboard">
        <thead>
          <tr>
            <SortHeader field="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Atleet</SortHeader>
            <SortHeader field="eventKm" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Evenement km</SortHeader>
            <SortHeader field="trainingKm" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Training km</SortHeader>
            <th>Donaties</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(({ athlete, trainingKm, eventKm, trainingCount, eventCount }) => (
            <tr
              key={athlete._id}
              className={athlete._id === athleteId ? "leaderboard-selected" : ""}
              onClick={() => {}}
            >
              <td>
                <Link to={`/athletes/${athlete._id}`} className="leaderboard-athlete">
                  {athlete.profilePhoto && (
                    <img src={athlete.profilePhoto} alt={athlete.firstName} className="leaderboard-photo" />
                  )}
                  {athlete.displayName || athlete.name}
                </Link>
              </td>
              <td className="leaderboard-num">
                {eventCount > 0 ? `${km(eventKm)} km (${eventCount})` : "—"}
              </td>
              <td className="leaderboard-num">
                {trainingCount > 0 ? `${km(trainingKm)} km (${trainingCount})` : "—"}
              </td>
              <td className="leaderboard-num">—</td>
            </tr>
          ))}
        </tbody>
      </table>

      {athleteId && selectedAthlete && (
        <div className="athlete-detail" ref={detailRef}>
          <div className="athlete-detail-header">
            {selectedAthlete.profilePhoto && (
              <img src={selectedAthlete.profilePhoto} alt={selectedAthlete.firstName} className="athlete-detail-photo" />
            )}
            <div>
              <h2>{selectedAthlete.displayName || selectedAthlete.name}</h2>
              <div className="athlete-stats">
                <div className="athlete-stat">
                  <span className="stat-value">{selectedActivities.length}</span>
                  <span className="stat-label">activiteiten</span>
                </div>
                <div className="athlete-stat">
                  <span className="stat-value">{km(selectedActivities.reduce((s, a) => s + (a.distance || 0), 0))}</span>
                  <span className="stat-label">km totaal</span>
                </div>
                {selectedEvents.length > 0 && (
                  <div className="athlete-stat">
                    <span className="stat-value">{km(selectedEvents.reduce((s, a) => s + (a.distance || 0), 0))}</span>
                    <span className="stat-label">km evenementen ({selectedEvents.length})</span>
                  </div>
                )}
                {selectedTrainings.length > 0 && (
                  <div className="athlete-stat">
                    <span className="stat-value">{km(selectedTrainings.reduce((s, a) => s + (a.distance || 0), 0))}</span>
                    <span className="stat-label">km trainingen ({selectedTrainings.length})</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {selectedEvents.length > 0 && (
            <section>
              <h3>Evenement activiteiten</h3>
              <table className="activity-table">
                <tbody>
                  {selectedEvents.map(a => (
                    <tr key={a._id}>
                      <td>{a.name}</td>
                      <td>{dateStr(a.date)}</td>
                      <td>{km(a.distance)} km</td>
                      <td>{dur(a.moving_time)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {selectedTrainings.length > 0 && (
            <section>
              <h3>Trainingen</h3>
              <table className="activity-table">
                <tbody>
                  {selectedTrainings.map(a => (
                    <tr key={a._id}>
                      <td>{a.name}</td>
                      <td>{dateStr(a.date)}</td>
                      <td>{km(a.distance)} km</td>
                      <td>{dur(a.moving_time)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
